<template>
  <section v-if="checkCards.length" :class="[$style.root, $style[`root_${addMargin}`]]">
    <div :class="$style.jewelsSwiper" ref="jewelsSwiper">
      <h2 :class="$style.jewelsSwiper__title">
        {{ data.acf_fields.txt_1 }}
        <span :class="$style.jewelsSwiper__title_span"> {{ data.acf_fields.txt_2 }}</span>
        {{ data.acf_fields.txt_3 }}
      </h2>
      <div :class="$style.jewelsSwiper__subTitle">
        <UiTheSectionSubtitle
          :title="{
            body: 'Katerina’s Picks: discover & shop her favourites.'
          }"
        />
      </div>

      <swiper
        :spaceBetween="20"
        :slidesPerView="'auto'"
        :freeMode="true"
        :pagination="{ clickable: true }"
        :speed="1000"
        :modules="[Navigation]"
        :navigation="true"
        :loop="true"
        @touchStart="startDragging"
        @touchEnd="stopDragging"
      >
        <swiper-slide v-for="(el, index) in checkCards" :key="el.brand_name" :class="$style.jewelsSwiper__slide">
          <button @click="clickHandler(index)" :class="$style.jewelsSwiper__card">
            <UiBaseImage
              :class="$style.jewelsSwiper__cardImg"
              :image="{
                alt: el.img_big.alt,
                sizes: {
                  img_big: {
                    url: el.img_big.url,
                    width: el.img_big.width,
                    height: el.img_big.height
                  },
                  img_small: {
                    url: el.img_big.url,
                    width: el.img_big.width,
                    height: el.img_big.height
                  }
                }
              }"
            />
            <h6 :class="$style.jewelsSwiper__cardTitle">{{ el.brand_name }}</h6>
            <p :class="$style.jewelsSwiper__cardText">
              {{ el.product_name }}
            </p>
            <p :class="$style.jewelsSwiper__cardText">{{ el.price }}</p>
          </button>
        </swiper-slide>
      </swiper>

      <!-- Перемещаем prompt внутрь jewelsSwiper -->
      <div ref="prompt" :class="$style.promptForCursor">
        <p :class="$style.promptForCursor__text">Drag</p>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation } from 'swiper/modules'
import 'swiper/css'
import { ref, onMounted, onUnmounted, computed } from 'vue'
import type SwiperType from 'swiper'

const nuxtApp = useNuxtApp()
const modalManager = nuxtApp.$modalManager as IModalManager
const isMobile = ref<boolean>(false)
const jewelsSwiper = ref<HTMLElement | null>(null)
const prompt = ref<HTMLElement | null>(null)
const isDragging = ref<boolean>(false)

type jewelCard = {
  img_preview: {
    url: string
    alt: string
    width: number
    height: number
    sizes: {
      thumbnail: string
      'thumbnail-height': string
      'thumbnail-width': string
    }
  }
  img_small: {
    url: string
    alt: string
    width: number
    height: number
    sizes: {
      thumbnail: string
      'thumbnail-height': string
      'thumbnail-width': string
    }
  }
  img_big: {
    url: string
    alt: string
    width: number
    height: number
    sizes: {
      thumbnail: string
      'thumbnail-height': string
      'thumbnail-width': string
    }
  }
  brand_name: string
  product_description: string
  product_name: string
  price: string
}

const props = defineProps<{
  data: {
    acf_fields: {
      txt_1: string
      txt_2: string
      txt_3: string
      cards_settings: jewelCard[]
    }
  }
  addMargin?: boolean
}>()

/**
 * Move the prompt element to the position of the event within the hover area,
 * without exceeding the boundaries of the hover area.
 */
const moveCursor = (event: MouseEvent): void => {
  if (isMobile.value || !jewelsSwiper.value || !prompt.value || isDragging.value) return

  requestAnimationFrame(() => {
    const promptEl = prompt.value
    const swiperEl = jewelsSwiper.value

    if (!promptEl) return

    if (promptEl?.style.opacity !== '1') {
      promptEl.style.opacity = '1'
    }

    if (!swiperEl) return
    const rect = swiperEl.getBoundingClientRect()
    const x = Math.min(Math.max(event.clientX - rect.left + 10, 0), rect.width - 20)
    const y = Math.min(Math.max(event.clientY - rect.top + 10, 0), rect.height - 20)

    promptEl.style.transform = `translate(${x}px, ${y}px)`
  })
}

const startDragging = () => {
  if (isMobile.value || !jewelsSwiper.value || !prompt.value) return
  isDragging.value = true
  prompt.value.style.opacity = '0' // Скрываем prompt при начале перетаскивания
  jewelsSwiper.value.removeEventListener('mousemove', moveCursor)
}

/**
 * Handles the touch end event of the Swiper component.
 *
 * @param {Swiper} swiper - The Swiper component emitting the event.
 * @param {MouseEvent | TouchEvent} event - The `touchEnd` event emitted by Swiper.
 * @returns {void}
 */
const stopDragging = (swiper: SwiperType, event: MouseEvent | TouchEvent): void => {
  if (isMobile.value || !jewelsSwiper.value || !prompt.value) return

  isDragging.value = false

  // Проверяем, находится ли курсор в области jewelsSwiper
  const rect = jewelsSwiper.value!.getBoundingClientRect()
  let clientX: number
  let clientY: number

  if (event instanceof TouchEvent) {
    // For TouchEvent, we need to access the touches array
    clientX = event.touches[0].clientX
    clientY = event.touches[0].clientY
  } else {
    // For MouseEvent, we can access clientX and clientY directly
    clientX = event.clientX
    clientY = event.clientY
  }

  const isInArea = clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom

  if (isInArea) {
    prompt.value!.style.opacity = '1' // Показываем, если курсор в области
    jewelsSwiper.value!.addEventListener('mousemove', moveCursor)
  } else {
    prompt.value!.style.opacity = '0' // Скрываем, если курсор вне области
    jewelsSwiper.value!.removeEventListener('mousemove', moveCursor)
  }
}

const checkCards = computed(() => props.data?.acf_fields?.cards_settings.filter((el: jewelCard) => el.img_big))

const clickHandler = (index: number) => {
  const BaseProductModal = defineAsyncComponent(() => import('~/components/modals/BaseProductModal.vue'))
  modalManager.showModal(markRaw(BaseProductModal) as object as ModalComponent, {
    shouldRedirectOnClose: false,
    hideCloseRightButton: true,
    props: {
      repeater: props.data?.acf_fields?.cards_settings,
      selected: index
    },
    centerPosY: !isMobile.value,
    isBottomPos: isMobile.value
  })
}

const updateIsMobile = () => {
  isMobile.value = window.innerWidth <= 1024
}

onMounted(() => {
  updateIsMobile()
  window.addEventListener('resize', updateIsMobile)

  if (jewelsSwiper.value && prompt.value) {
    jewelsSwiper.value.addEventListener('mouseover', () => {
      if (!isDragging.value) {
        prompt.value!.style.opacity = '1'
        jewelsSwiper.value!.addEventListener('mousemove', moveCursor)
      }
    })

    jewelsSwiper.value.addEventListener('mouseout', () => {
      if (!isDragging.value) {
        prompt.value!.style.opacity = '0'
        jewelsSwiper.value!.removeEventListener('mousemove', moveCursor)
      }
    })
  }

  // eslint-disable-next-line
  // @ts-ignore
  navigator.getBattery?.().then((battery) => {
    if (battery.level < 0.2) {
      // Уменьшить частоту обновлений или отключить анимацию
      jewelsSwiper.value!.removeEventListener('mousemove', moveCursor)
      if (prompt.value) {
        prompt.value.style.display = 'none'
      }
      return
    }
  })
})

onUnmounted(() => {
  window.removeEventListener('resize', updateIsMobile)
  if (jewelsSwiper.value) {
    jewelsSwiper.value.removeEventListener('mousemove', moveCursor)
  }
})
</script>

<style lang="scss" module>
.root {
  margin: var(--gutter-desktop) auto 0 auto;
  position: relative;

  &_true {
    margin-left: 8rem;
    margin-right: 8rem;
  }

  .jewelsSwiper {
    padding: 4.6875rem 0;
    border-radius: 0.625rem;
    border: 1px solid v.$color-grey;
    position: relative;

    &__title {
      @include t.headline-h3;
      span {
        @include t.headline-light-h3;
      }

      & {
        margin-bottom: 0.3125rem;
        text-align: center;
      }
    }

    &__subTitle {
      margin-bottom: 3.125rem;
    }

    &__card {
      &:hover {
        cursor: grab;
      }

      border: none;
      background: transparent;
      text-decoration: none;
      color: v.$color-black;
    }

    &__slide {
      width: 18.75rem;

      @include v.to(mobileSmall) {
        width: 100%;
      }
    }

    &__cardImg {
      border-radius: 0.625rem;
      margin-bottom: 0.8rem;
      width: 18.75rem;
      height: 18.75rem;
      object-fit: cover;
      display: block;
      position: relative;
      overflow: hidden;

      background-color: rgba(0, 0, 0, 0.05);

      img {
        margin-bottom: -4px;
        mix-blend-mode: multiply;
        object-fit: cover;
      }

      @include v.to(mobile) {
        margin: 0 auto;
        margin-bottom: 0.8rem;
      }
    }

    &__cardTitle {
      @include t.text-question;
    }

    &__cardText {
      @include t.text-default;
      text-decoration: none;
      color: v.$color-black;
    }

    :global {
      .swiper {
        width: 100%;
        height: 100%;
        padding: 0 1.5rem;
      }

      .swiper-slide {
        text-align: center;
      }
    }

    :global(.swiper-button-prev),
    :global(.swiper-button-next) {
      width: 2.5rem;
      height: 2.5rem;
      border-radius: 100%;
      border: 0.8px solid v.$color-grey;
      backdrop-filter: blur(10px);
      background: #ffffff66;
      position: absolute;
      top: 50%;
      z-index: 3;

      @include v.from(mobile) {
        display: none;
      }

      &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    }

    :global(.swiper-button-prev) {
      transform: translateY(-50%);
      left: 0.2rem;
      &::after {
        content: '';
        width: 0.75rem;
        height: 0.25rem;
        font-size: 0;
        background: url('assets/icons/post/arrow.svg') center center no-repeat;
      }
    }

    :global(.swiper-button-next) {
      transform: translateY(-50%);
      right: 0.2rem;
      &::after {
        width: 0.75rem;
        height: 0.25rem;
        font-size: 0;
        background: url('assets/icons/post/arrow.svg') center center no-repeat;
        transform: translate(-50%, -50%) rotate(180deg);
      }
    }

    @include v.to(mobile) {
      margin: 0;
      padding: 0 0 2.5rem;

      &__title {
        & {
          padding: 2.5rem 2.1875rem 0 2.1875rem;
          margin-bottom: 0.3125rem;
        }
        &_span {
          display: block;
        }
      }

      :global {
        .swiper {
          padding: 0 2.1875rem;
        }
      }
    }
  }

  .promptForCursor {
    position: absolute;
    pointer-events: none;
    z-index: 99;
    opacity: 0;
    left: 0;
    top: 0;
    padding: 0.375rem 0.75rem;
    border-radius: 1.875rem;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(10px);
    border: 1px solid v.$color-grey;
    transition: opacity 0.2s ease, left 0.1s ease, top 0.1s ease;
    will-change: transform, opacity;

    &__text {
      @include t.text-button-small;
    }

    @include v.to(mobile) {
      display: none;
    }
  }

  @include v.to(tablet) {
    margin: var(--gutter-mobile) 0 0;
  }
}
</style>
